import { useMemo, useState, useEffect } from "react";
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.body.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.body.classList.contains("dark"))
    );
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

function getMonthKey(dateStr) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthLabel(key) {
  const [year, month] = key.split("-");
  const d = new Date(parseInt(year), parseInt(month) - 1, 1);
  return `${d.toLocaleString("en-US", { month: "short" })} '${String(d.getFullYear()).slice(2)}`;
}

function getMonthLabelFull(key) {
  const [year, month] = key.split("-");
  const d = new Date(parseInt(year), parseInt(month) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function ratingColor(avg) {
  if (avg >= 4) return "#10b981";
  if (avg >= 3) return "#f59e0b";
  return "#ef4444";
}

function CustomDot(props) {
  const { cx, cy, payload, isDark, isMobile } = props;
  if (!payload || payload.avg == null || cx == null || cy == null) return null;
  const c  = ratingColor(payload.avg);
  const bg = isDark ? "#1a1730" : "#ffffff";
  const r  = isMobile ? 4 : 6;
  const ri = isMobile ? 1.8 : 2.5;
  return (
    <g key={`dot-${cx}-${cy}`}>
      <circle cx={cx} cy={cy} r={r}  fill={bg} stroke={c} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={ri} fill={c} />
    </g>
  );
}

function CustomActiveDot(props) {
  const { cx, cy, payload, isDark, isMobile } = props;
  if (!payload || payload.avg == null || cx == null || cy == null) return null;
  const c  = ratingColor(payload.avg);
  const bg = isDark ? "#1a1730" : "#ffffff";
  const r1 = isMobile ? 8 : 11;
  const r2 = isMobile ? 5 : 7;
  const r3 = isMobile ? 2 : 3;
  return (
    <g key={`adot-${cx}-${cy}`}>
      <circle cx={cx} cy={cy} r={r1} fill={c} fillOpacity={0.15} />
      <circle cx={cx} cy={cy} r={r2} fill={bg} stroke={c} strokeWidth={2} />
      <circle cx={cx} cy={cy} r={r3} fill={c} />
    </g>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const c    = ratingColor(d.avg);
  const full = Math.round(d.avg);
  const stars = "★".repeat(full) + "☆".repeat(5 - full);
  return (
    <div className="rtc-tooltip">
      <div className="rtc-tooltip-month">{d.full}</div>
      <div className="rtc-tooltip-row">
        <span className="rtc-tooltip-stars" style={{ color: c }}>{stars}</span>
        <span className="rtc-tooltip-val"   style={{ color: c }}>{d.avg.toFixed(1)}</span>
      </div>
      <div className="rtc-tooltip-meta">
        <span>{d.posPct}% positive</span>
        <span className="rtc-tooltip-sep">·</span>
        <span>{d.count} review{d.count !== 1 ? "s" : ""}</span>
      </div>
    </div>
  );
}

function RatingTrendChart({ reviews }) {
  const isDark   = useIsDark();
  const isMobile = useIsMobile();

  const { data, overallAvg, overallPos, periodLabel, monthCount } = useMemo(() => {
    const empty = { data: [], overallAvg: null, overallPos: null, periodLabel: "", monthCount: 0 };
    if (!reviews || reviews.length === 0) return empty;
    const dated = reviews.filter((r) => r.date);
    if (dated.length === 0) return empty;

    const buckets = {};
    dated.forEach((r) => {
      const key = getMonthKey(r.date);
      if (!buckets[key]) buckets[key] = { sum: 0, count: 0, pos: 0 };
      buckets[key].sum   += r.rating;
      buckets[key].count += 1;
      if (r.rating >= 4) buckets[key].pos += 1;
    });

    const sorted = Object.keys(buckets).sort();
    const points = sorted.map((key) => {
      const b   = buckets[key];
      const avg = parseFloat((b.sum / b.count).toFixed(2));
      return {
        label:  getMonthLabel(key),
        full:   getMonthLabelFull(key),
        avg,
        posPct: Math.round((b.pos / b.count) * 100),
        count:  b.count,
      };
    });

    const totalSum = dated.reduce((s, r) => s + r.rating, 0);
    const totalPos = dated.filter((r) => r.rating >= 4).length;
    const overallAvg  = parseFloat((totalSum / dated.length).toFixed(1));
    const overallPos  = Math.round((totalPos / dated.length) * 100);
    const first       = getMonthLabel(sorted[0]);
    const last        = getMonthLabel(sorted[sorted.length - 1]);
    const periodLabel = sorted.length > 1 ? `${first} – ${last}` : first;

    return { data: points, overallAvg, overallPos, periodLabel, monthCount: sorted.length };
  }, [reviews]);

  if (data.length < 2) return null;

  // On mobile: show ~4 X-axis labels max; on desktop ~10
  const tickInterval = isMobile
    ? Math.max(1, Math.ceil(data.length / 4) - 1)
    : (data.length > 12 ? Math.floor(data.length / 10) : 0);

  // Dense data on mobile: hide static dots to avoid overlap (active dot still shows on tap)
  const showStaticDots = isMobile ? data.length <= 18 : true;

  const lineStroke  = isDark ? "#e0e7ff" : "#1e1b4b";
  const gridStroke  = isDark ? "#2d2a4a" : "#b8b2ee";
  const tickColor   = isDark ? "#6b65a0" : "#7c74c9";
  const fillColor   = isDark ? "#a5b4fc" : "#818cf8";
  const fillOpacity = isDark ? 0.45 : 0.22;
  const refGreen    = isDark ? "#166534" : "#bbf7d0";
  const refAmber    = isDark ? "#92400e" : "#fde68a";

  const chartMargin = isMobile
    ? { top: 10, right: 38, left: 4, bottom: 0 }
    : { top: 14, right: 10, left: -28, bottom: 0 };

  const refLabelStyle = {
    fontFamily: "Plus Jakarta Sans, system-ui",
    fontWeight: 600,
  };

  return (
    <div className="rtc-card">
      <div className="rtc-header">
        <div className="rtc-header-left">
          <span className="rtc-title">Rating Trend</span>
          <span className="rtc-period">{periodLabel}</span>
        </div>
        <div className="rtc-stats">
          <span className="rtc-stat-pill rtc-stat-rating">★ {overallAvg}</span>
          <span className="rtc-stat-pill rtc-stat-pos">{overallPos}% positive</span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={isMobile ? 160 : 190}>
        <ComposedChart data={data} margin={chartMargin}>
          <defs>
            <linearGradient id="ratingGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={fillColor} stopOpacity={fillOpacity} />
              <stop offset="95%" stopColor={fillColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />

          {/* Reference lines — on mobile show inline label so Y-axis can be hidden */}
          <ReferenceLine y={4} stroke={refGreen} strokeDasharray="5 4" strokeWidth={1}
            label={isMobile ? { value: "4★", position: "right", fontSize: 9, fill: isDark ? "#4ade80" : "#16a34a", ...refLabelStyle } : undefined}
          />
          <ReferenceLine y={3} stroke={refAmber} strokeDasharray="5 4" strokeWidth={1}
            label={isMobile ? { value: "3★", position: "right", fontSize: 9, fill: isDark ? "#fcd34d" : "#d97706", ...refLabelStyle } : undefined}
          />

          <XAxis
            dataKey="label"
            tick={{ fontSize: isMobile ? 9 : 10, fill: tickColor, fontFamily: "Plus Jakarta Sans, system-ui" }}
            tickLine={false}
            axisLine={false}
            interval={tickInterval}
            padding={isMobile ? { left: 12, right: 0 } : { left: 0, right: 0 }}
          />

          {/* Desktop: show Y-axis. Mobile: hide it (width 0) — reference line labels replace it */}
          <YAxis
            domain={[1, 5]}
            ticks={[1, 2, 3, 4, 5]}
            tick={{ fontSize: 10, fill: tickColor, fontFamily: "Plus Jakarta Sans, system-ui" }}
            tickLine={false}
            axisLine={false}
            width={isMobile ? 0 : undefined}
          />

          <Tooltip content={<CustomTooltip />} />

          <Area
            type="monotone"
            dataKey="avg"
            stroke="none"
            fill="url(#ratingGrad)"
            connectNulls={false}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

          <Line
            type="monotone"
            dataKey="avg"
            stroke={lineStroke}
            strokeWidth={isMobile ? 2 : 3}
            connectNulls={false}
            dot={showStaticDots ? (props) => <CustomDot {...props} isDark={isDark} isMobile={isMobile} /> : false}
            activeDot={(props) => <CustomActiveDot {...props} isDark={isDark} isMobile={isMobile} />}
            isAnimationActive={true}
            animationDuration={900}
            animationEasing="ease-out"
          />
        </ComposedChart>
      </ResponsiveContainer>

      <div className="rtc-footer">
        <span className="rtc-foot-item">
          <span className="rtc-foot-dot rtc-foot-green" />
          <span className="rtc-foot-label">4–5★ good</span>
        </span>
        <span className="rtc-foot-sep">·</span>
        <span className="rtc-foot-item">
          <span className="rtc-foot-dot rtc-foot-amber" />
          <span className="rtc-foot-label">3★ average</span>
        </span>
        <span className="rtc-foot-sep">·</span>
        <span className="rtc-foot-item">
          <span className="rtc-foot-dot rtc-foot-red" />
          <span className="rtc-foot-label">1–2★ poor</span>
        </span>
        <span className="rtc-foot-count">{monthCount} months with data</span>
      </div>
    </div>
  );
}

export default RatingTrendChart;
